import { useEffect, useState } from 'react';
import EntityType from '@/shared/enums/EntityType';
import { encode, decode } from '@/shared/utils/encoder';
import useAuthStore from '@pkg/auth/useStore';
import useComparison from '@/components/DesignContainer/hooks/useComparison';
import defaultChartSettings from '../utils/defaultChartSettings';

const getComparisonSettings = (xAxis) => {
  const { designId, revisionId, designType, designEntityId } = xAxis;

  return {
    designId,
    revisionId,
    entity: {
      uuid: designEntityId || designId,
      type: designType || EntityType.ORGANISATION,
    },
  };
};

const useChartSettings = ({ id }) => {
  const organisation = useAuthStore((state) => state.organisation);
  const [settings, setSettings] = useState();
  const { activeComparison, loadComparison, setActiveComparison } =
    useComparison();

  const handleChartSelect = (id) => {
    setSettings({
      ...settings,
      chartType: id,
    });
  };

  const handleSettingsUpdate = (update, name) => {
    const { id, ...updatedSettings } = {
      ...settings,
      [name]: {
        ...settings[name],
        ...update,
      },
    };

    updatedSettings.id = encode(updatedSettings);

    setSettings(updatedSettings);
  };

  const handleConditionUpdate = (condition) => {
    const { id, ...updatedSettings } = {
      ...settings,
      condition,
    };

    updatedSettings.id = encode(updatedSettings);

    setSettings(updatedSettings);
  };

  const handleXAxisUpdate = (xAxis, index = 0) => {
    const update = [...settings.xAxis];

    const resetCondition = xAxis?.entityType !== settings?.xAxis[0]?.entityType;

    if (update[index]) {
      update[index] = {
        ...update[index],
        ...xAxis,
      };
    } else {
      update.push(xAxis);
    }

    const { id, ...updatedSettings } = {
      ...settings,
      xAxis: update,
      condition: resetCondition ? null : settings.condition,
    };

    updatedSettings.id = encode(updatedSettings);

    setSettings(updatedSettings);
  };

  const handleYAxisUpdate = (yAxis) => {
    const update = {
      ...settings.yAxis,
      ...yAxis,
    };

    const { id, ...updatedSettings } = {
      ...settings,
      yAxis: update,
    };

    updatedSettings.id = encode(updatedSettings);

    setSettings(updatedSettings);
  };

  useEffect(() => {
    if (!id) {
      const { id, ...chartSettings } = defaultChartSettings({
        designId: organisation?.design?.uuid,
        revisionId: 'latest',
        designType: EntityType.ORGANISATION,
      });

      chartSettings.id = encode(chartSettings);

      setSettings(chartSettings);
      if (!activeComparison) {
        const comparisonSettings = {
          ...chartSettings.xAxis[0],
        };

        if (comparisonSettings.entityType === EntityType.ROLE_TYPE) {
          comparisonSettings.entityType = EntityType.ROLE;
        }
        if (comparisonSettings.entityType === EntityType.ACTIVITY_TYPE) {
          comparisonSettings.entityType = EntityType.ACTIVITY;
        }

        const { designId, revisionId, entity } =
          getComparisonSettings(comparisonSettings);

        loadComparison(designId, revisionId, entity);
      }
      return;
    }

    if (id !== settings?.id) {
      const loadedSettings = decode(id);

      setSettings({
        ...loadedSettings,
        id,
      });

      if (!activeComparison) {
        const { designId, revisionId, entity } = getComparisonSettings(
          loadedSettings.xAxis[0]
        );

        loadComparison(designId, revisionId, entity);
      }
    }
  }, [id]);

  return {
    chartSelect: handleChartSelect,
    conditionUpdate: handleConditionUpdate,
    settings,
    sources: activeComparison ? [activeComparison] : [],
    settingsUpdate: handleSettingsUpdate,
    xAxisUpdate: handleXAxisUpdate,
    yAxisUpdate: handleYAxisUpdate,
  };
};

export default useChartSettings;
